/**
 * Availability Redux Sagas
 */

import { call, put, takeLatest, all, fork, select, type SagaReturnType } from '@spcc/core'
import { availabilityActions } from './availability.slice'
import { selectPendingChanges, selectExistingRecords } from './availability.selectors'
import { availabilityApi } from '../infrastructure/api'
import type { AvailabilityChange } from '../domain/types'

// ============================================================================
// Fetch Matrix Saga
// ============================================================================

function* fetchMatrixSaga(): Generator {
  try {
    const result: SagaReturnType<typeof availabilityApi.getAvailabilityMatrix> = yield call(
      availabilityApi.getAvailabilityMatrix
    )
    yield put(availabilityActions.fetchMatrixSuccess(result))
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch availability matrix'
    yield put(availabilityActions.fetchMatrixFailure(message))
  }
}

// ============================================================================
// Save Changes Saga
// ============================================================================

function* saveChangesSaga(): Generator {
  try {
    // Get pending changes and existing records from state
    const pendingChanges: SagaReturnType<typeof selectPendingChanges> = yield select(selectPendingChanges)
    const existingRecords: SagaReturnType<typeof selectExistingRecords> = yield select(selectExistingRecords)

    if (pendingChanges.length === 0) {
      yield put(availabilityActions.saveChangesSuccess())
      return
    }

    // Build a lookup map from existing DB records so we can directly update instead of
    // making an extra findAvailabilityRecord API call per change (which is fragile in Strapi 5)
    const recordMap = new Map<string, string>() // `factoryId:variantId` → documentId
    existingRecords.forEach((record) => {
      if (record.factoryDocumentId && record.variantDocumentId) {
        recordMap.set(`${record.factoryDocumentId}:${record.variantDocumentId}`, record.documentId)
      }
    })

    // Resolve each pending change with its existing record documentId (if any)
    const changes = pendingChanges.map((c) => ({
      factoryDocumentId: c.factoryDocumentId,
      variantDocumentId: c.variantDocumentId,
      isAvailable: c.isAvailable,
      existingDocumentId: recordMap.get(`${c.factoryDocumentId}:${c.variantDocumentId}`) ?? null,
    }))

    // Call bulk update API
    const result: SagaReturnType<typeof availabilityApi.bulkUpdateAvailability> = yield call(
      availabilityApi.bulkUpdateAvailability,
      changes
    )

    if (result.failed > 0) {
      yield put(
        availabilityActions.saveChangesFailure(
          `Saved ${result.success} changes, but ${result.failed} failed.`
        )
      )
    } else {
      yield put(availabilityActions.saveChangesSuccess())
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to save changes'
    yield put(availabilityActions.saveChangesFailure(message))
  }
}

// ============================================================================
// Watcher
// ============================================================================

function* watchAvailability() {
  yield takeLatest(availabilityActions.fetchMatrix.type, fetchMatrixSaga)
  yield takeLatest(availabilityActions.saveChanges.type, saveChangesSaga)
}

// ============================================================================
// Root Saga
// ============================================================================

export function* availabilitySaga() {
  yield all([fork(watchAvailability)])
}
